// Instructions:
// If you use the prop formik the edit button will show on the panel.
// To activate the form fields in your component use the usePanelContext like this:
// const panelId = 'invoice-summary'
// const { isEdit } = usePanelContext()
// <InputNumber ... disabled={!isEdit(panelId)} />
import React, { useEffect } from 'react'

import { FormikProps } from 'formik'
import _ from 'lodash'
import { useLocalstorageState } from 'rooks'

import { useNavBlockerContext } from '../../contexts'
import { usePanelContext } from '../../contexts/panel'
import { Constants } from '../../utils/constants'
import { Panel } from './Panel'

type Props = {
  children: JSX.Element
  formik?: FormikProps<any>
  header: string
  id: string
  toggleable?: boolean
  visible?: boolean
  isRequestRunning?: boolean
  hasChangesPending?: boolean
  buttons?: JSX.Element
}

export const PanelContainer: React.FC<Props> = ({
  children,
  formik,
  header,
  id,
  toggleable = true,
  visible = true,
  isRequestRunning = false,
  hasChangesPending = false,
  buttons,
}) => {
  const { setBlocker } = useNavBlockerContext()
  const { addId, removeId, isEdit } = usePanelContext()

  const [isCollapsed, setIsCollapsed] = useLocalstorageState<any>(
    Constants.LOCAL_STORAGE_PANELS_KEY,
    { [id]: false },
  )

  // Block navigation when the form is dirty
  useEffect(() => {
    if (formik) {
      setBlocker(formik.dirty)
    }
  }, [formik?.dirty])

  // cancel
  function handleCancel() {
    formik?.resetForm()
    removeId(id)
  }

  // toggle
  function handleToggle() {
    const newIsCollapsed = {
      ...isCollapsed,
      [id]: !isCollapsed[id],
    }
    setIsCollapsed(newIsCollapsed)
  }

  // edit
  function handleEdit() {
    const newIsCollapsed = {
      ...isCollapsed,
      [id]: false,
    }
    setIsCollapsed(newIsCollapsed)

    addId(id)
  }

  // submit
  async function handleSubmit() {
    await formik?.submitForm()

    // check for errors before deactivating the editmode
    if (_.isEmpty(formik?.errors)) {
      removeId(id)
    }
  }

  return (
    <div id={id}>
      <Panel
        children={children}
        formik={formik}
        handleCancel={handleCancel}
        handleEdit={handleEdit}
        handleSubmit={handleSubmit}
        handleToggle={handleToggle}
        header={header}
        visible={visible}
        toggleable={toggleable}
        isCollapsed={isCollapsed[id]}
        isEdit={isEdit(id)}
        isRequestRunning={isRequestRunning}
        hasChangesPending={hasChangesPending}
        buttons={buttons}
      />
    </div>
  )
}
